import React from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { PageSlice, SliceGridDetails, SliceGridItem } from '@contracts';
import Colors from '../Colors';
import LazyLoadImage from '../LazyLoadImage';
import Text from '../Text';
import TextLink from '../TextLink';
import useDesktop from '../useDesktop';
import { TEXT_STYLES, TextVariant } from './Typography';

interface GridProps {
  slice: PageSlice;
}

const GridVariant2: React.FC<GridProps> = ({ slice }) => {
  const sliceDetails = slice.primary as SliceGridDetails;
  const sliceItems = slice.items as SliceGridItem[];
  const desktop = useDesktop(450);
  const { t } = useTranslation();

  return (
    <Wrapper>
      {sliceDetails?.overline && <Overline>{sliceDetails.overline}</Overline>}
      {sliceDetails?.heading?.text && <Heading>{sliceDetails.heading.text}</Heading>}
      {sliceDetails?.subheading?.text && <SubHeading>{sliceDetails.subheading.text}</SubHeading>}
      <GridWrap>
        {sliceItems.map((item, index) => (
          <GridItem key={index}>
            {item?.image?.url ? <LazyLoadImage src={item.image.url} alt={item.image.alt || ''} /> : null}
            <GridItemBody>
              {item?.heading?.text && (
                <h4>
                  <Text
                    richText={
                      desktop || !item?.mobile_heading?.richText?.length
                        ? item.heading.richText
                        : item?.mobile_heading?.richText || item.heading.richText
                    }
                  />
                </h4>
              )}
              {item?.bodycopy?.text && <p>{desktop ? item.bodycopy.text : item?.mobile_bodycopy?.text || item.bodycopy.text}</p>}
              {item?.supbodycopy?.text && <span>{item.supbodycopy.text}</span>}
              {item?.learn_more_url && (
                <LearnMoreLink to={item.learn_more_url} $color={Colors.navyBlue}>
                  {t('components.learn-more')}
                </LearnMoreLink>
              )}
            </GridItemBody>
          </GridItem>
        ))}
      </GridWrap>
    </Wrapper>
  );
};

export default GridVariant2;

const Wrapper = styled.div`
  width: 90vw;
  max-width: 1200px;
  margin: 0 auto;
  padding: 96px 0;
  text-align: center;

  @media (max-width: 450px) {
    padding: 56px 0;
  }
`;

const Overline = styled.div`
  ${TEXT_STYLES[TextVariant.UpperText4]};
  text-align: center;
  margin: 0 auto 16px;
`;

const Heading = styled.h2`
  ${TEXT_STYLES[TextVariant.Heading4]};
  text-align: center;
  margin: 0 auto 16px;

  @media (max-width: 800px) {
    padding: 0 50px;
  }

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.Heading5]};
  }
`;

const SubHeading = styled.h2`
  ${TEXT_STYLES[TextVariant.BodyRegularLG]};
  text-align: center;
  max-width: 640px;
  margin: 0 auto;

  @media (max-width: 450px) {
    ${TEXT_STYLES[TextVariant.BodyRegularMD]};
  }
`;

const GridWrap = styled.div`
  display: flex;
  flex-wrap: nowrap;
  flex-direction: row;
  justify-content: center;
  align-items: stretch;
  column-gap: 32px;
  margin-top: 32px;

  @media (max-width: 1024px) {
    column-gap: 16px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
    row-gap: 32px;
    align-items: stretch;
  }

  @media (max-width: 600px) {
    row-gap: 16px;
    margin-top: 24px;
  }
`;

const GridItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: ${Colors.paleGold25};
  padding: 24px;
  max-width: 360px;
  margin: 0;
  flex: 1;
  border-radius: 12px;

  @media (max-width: 800px) {
    flex-direction: row;
    column-gap: 0px;
    align-items: stretch;
    max-width: 90vw;
    padding: 24px;
    text-align: left;
  }

  img {
    width: 124px;
    height: 124px;
    object-fit: cover;

    @media (max-width: 800px) {
      position: absolute;
      width: 50px;
      height: 50px;
    }
  }
`;

const GridItemBody = styled.div`
  padding: 24px 0 0 0;
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  border-radius: 0 0 12px 12px;
  flex: 1;

  @media (max-width: 800px) {
    padding: 0;
  }

  > p {
    ${TEXT_STYLES[TextVariant.BodyRegularLG]};
    color: ${Colors.navyBlue};
    margin: 0;
    letter-spacing: -0.1px;

    @media (max-width: 450px) {
      ${TEXT_STYLES[TextVariant.BodyRegularMD]};
    }
  }

  > span {
    letter-spacing: 0;
    ${TEXT_STYLES[TextVariant.BodyBoldMD]};
    color: ${Colors.navyBlue};
    margin: 0;

    @media (max-width: 450px) {
      ${TEXT_STYLES[TextVariant.BodyBoldSM]};
    }
  }

  > h4 {
    height: 50px;

    @media (max-width: 800px) {
      padding-left: 66px;
      display: flex;
      align-items: center;
      height: 50px;
    }

    p {
      margin: 0;
      text-align: center;
      ${TEXT_STYLES[TextVariant.Heading7]};

      @media (max-width: 800px) {
        text-align: left;
      }

      @media (max-width: 450px) {
        ${TEXT_STYLES[TextVariant.Heading7]};
      }

      @media (max-width: 350px) {
        ${TEXT_STYLES[TextVariant.Heading8]};
      }
    }
  }
`;

const LearnMoreLink = styled(TextLink)`
  margin-top: 5px;
  align-self: center;
  font-size: 15px;

  @media (max-width: 800px) {
    align-self: start;
  }
`;
